<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="8">
            <v-card>
                <v-card-title>
                    <h2>{{ data.labelText?.createPageTitle || 'Create Master Data' }}</h2>
                </v-card-title>

                <v-card-text>
                    <!-- Alert Messages -->
                    <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>
                    <alert-message
                        ref="successAlert"
                        type="success"
                        :message="data.successAlertMessage"
                    ></alert-message>

                    <!-- Form -->
                    <v-form ref="form" v-model="data.formValid" v-if="data.config">
                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="data.formData.code"
                                    :label="data.labelText?.codeLabel || 'Code'"
                                    :rules="data.codeRules"
                                    required
                                    variant="outlined"
                                    density="comfortable"
                                    :disabled="data.submitting"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-text-field
                                    v-model="data.formData.name"
                                    :label="data.labelText?.nameLabel || 'Name'"
                                    :rules="data.nameRules"
                                    required
                                    variant="outlined"
                                    density="comfortable"
                                    :disabled="data.submitting"
                                ></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-btn text @click="onBackClick" :disabled="data.submitting"> Back to List </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        @click="onCreateClick"
                        :loading="data.submitting"
                        :disabled="!data.formValid || data.submitting"
                    >
                        Create
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { PathDefinition } from '../../utils/PathDefinition';
import AlertMessage from '../../components/AlertMessage.vue';
import { MASTER_DATA_CONFIG } from '../../utils/headerHelper/MasterDataHeaderHelper';
import { MasterDataLabelTextHelper } from '../../utils/labelTextHelper/MasterDataLabelTextHelper';

export default defineComponent({
    name: 'CreateMasterData',

    components: {
        AlertMessage
    },

    setup() {
        const data = reactive({
            type: '',
            config: null as any,
            labelText: null as any,
            formValid: false,
            submitting: false,
            errorAlertMessage: '',
            successAlertMessage: '',
            formData: {
                code: '',
                name: ''
            },
            codeRules: [
                (v: string) => !!v || 'Code is required',
                (v: string) =>
                    /^[a-zA-Z0-9_-]+$/.test(v) || 'Code must contain only letters, numbers, hyphens, and underscores'
            ],
            nameRules: [(v: string) => !!v || 'Name is required']
        });

        return {
            data
        };
    },

    mounted() {
        // Get type from route params
        this.data.type = this.$route.params.type as string;

        // Validate type
        if (!MASTER_DATA_CONFIG[this.data.type]) {
            this.data.errorAlertMessage = `Invalid master data type: ${this.data.type}`;
            this.$refs.errorAlert?.showAlert();
            return;
        }

        // Initialize config and labels
        this.data.config = MASTER_DATA_CONFIG[this.data.type];
        this.data.labelText = MasterDataLabelTextHelper.getLabelTextForType(this.data.type);
    },

    methods: {
        async onCreateClick() {
            // Validate form
            const form = this.$refs.form as any;
            const { valid } = await form.validate();

            if (!valid) {
                return;
            }

            this.data.submitting = true;
            this.data.errorAlertMessage = '';
            this.data.successAlertMessage = '';

            try {
                const requestUrl = `/api/staff/${this.data.config.apiPath}/create`;
                const requestData = {
                    [this.data.config.codeField]: this.data.formData.code,
                    [this.data.config.nameField]: this.data.formData.name
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                if (response.success) {
                    const successMessages = MasterDataLabelTextHelper.getSuccessMessages(this.data.type);
                    this.data.successAlertMessage = successMessages.created;
                    this.$refs.successAlert?.showAlert();

                    // Wait a moment then navigate back
                    setTimeout(async () => {
                        await this.$router.push({ path: PathDefinition.masterData });
                    }, 1500);
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to create record';
                    this.$refs.errorAlert?.showAlert();
                }
            } catch (error) {
                console.error('Error creating master data:', error);
                this.data.errorAlertMessage = 'An unexpected error occurred';
                this.$refs.errorAlert?.showAlert();
            } finally {
                this.data.submitting = false;
            }
        },

        async onBackClick() {
            await this.$router.push({ path: PathDefinition.masterData });
        }
    }
});
</script>

<style scoped></style>
